import { Input, Switch } from "@arco-design/web-react";
import { useEffect, useState } from "react";
import { quoteBudgetPeopleInputError } from "../service/quoteBudgetPeopleService";

import type { QuoteBudgetEditorDraft } from "../runtime/useQuoteBudgetEditorRuntime";

/** 人数面板属性。值来自运行时草稿，回调直连运行时 setter. */
export type QuotePeopleCountPanelProps = {
  value: QuoteBudgetEditorDraft;
  disabled?: boolean;
  inputs?: Record<string, string>;
  onInputChange?: (label: string, text: string) => void;
  onStudentCountChange: (value: number) => void;
  onTeacherCountChange: (value: number) => void;
  onGuideCountChange: (value: number) => void;
  onDriverCountChange: (value: number) => void;
  onTeacherPaysChange: (value: boolean) => void;
};

/** 单个人数输入项. */
function CountField(props: {
  label: string;
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  text?: string;
  onInputChange?: (text: string) => void;
}) {
  const [localText, setLocalText] = useState(String(props.value));
  useEffect(() => setLocalText(String(props.value)), [props.value]);
  const text = props.text ?? localText;
  const error = quoteBudgetPeopleInputError(text);
  return (
    <label className="quote-budget-people__field">
      <span>{props.label}</span>
      <Input
        inputMode="numeric"
        aria-label={props.label}
        value={text}
        error={Boolean(error)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `qb-count-${props.label}` : undefined}
        disabled={props.disabled}
        onChange={(next) => {
          if (props.disabled) return;
          setLocalText(next);
          props.onInputChange?.(next);
          if (!quoteBudgetPeopleInputError(next)) props.onChange(Number(next));
        }}
      />
      {error ? <small id={`qb-count-${props.label}`} role="alert">{error}</small> : null}
    </label>
  );
}

/** 人数口径面板. */
export function QuotePeopleCountPanel(props: QuotePeopleCountPanelProps) {
  const { value, disabled } = props;
  return (
    <section className="quote-budget-people" aria-label="人数面板">
      <div className="quote-budget-people__title">人口口径</div>
      <CountField
        label="学生人数"
        text={props.inputs?.["学生人数"]}
        onInputChange={(text) => props.onInputChange?.("学生人数", text)}
        value={value.studentCount}
        disabled={disabled}
        onChange={props.onStudentCountChange}
      />
      <CountField
        label="老师人数"
        text={props.inputs?.["老师人数"]}
        onInputChange={(text) => props.onInputChange?.("老师人数", text)}
        value={value.teacherCount}
        disabled={disabled}
        onChange={props.onTeacherCountChange}
      />
      <CountField
        label="导游人数"
        text={props.inputs?.["导游人数"]}
        onInputChange={(text) => props.onInputChange?.("导游人数", text)}
        value={value.guideCount}
        disabled={disabled}
        onChange={props.onGuideCountChange}
      />
      <CountField
        label="司机人数"
        text={props.inputs?.["司机人数"]}
        onInputChange={(text) => props.onInputChange?.("司机人数", text)}
        value={value.driverCount}
        disabled={disabled}
        onChange={props.onDriverCountChange}
      />
      <div className="quote-budget-people__teacher-pays">
        <span>老师是否出钱</span>
        <Switch
          checked={value.teacherPays}
          disabled={disabled}
          aria-label="老师是否出钱"
          onChange={(checked) => { if (!disabled) props.onTeacherPaysChange(checked); }}
        />
        <strong>{value.teacherPays ? "是" : "否"}</strong>
      </div>
    </section>
  );
}
